import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Webinar } from "../../webinars/types";
import { confirmationEmail, isSafeMeetingLink, joiningInstructionsEmail } from "../../webinars/shared/emailTemplates";
import { REGISTRATION_CONTACT } from "../../webinars/config";

interface EmailPreviewProps {
  webinar: Webinar;
  meetingLink: string;
  /** Used to fill in the example; any attendee of the webinar */
  sample: { firstName: string; reference: string };
  onClose: () => void;
}

// Shows the emails exactly as the server will send them: both use the same template module
export default function EmailPreview({ webinar, meetingLink, sample, onClose }: EmailPreviewProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const canShowJoining = isSafeMeetingLink(meetingLink);
  const [tab, setTab] = useState<"joining" | "confirmation">(canShowJoining ? "joining" : "confirmation");

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const data = { ...sample, webinar, contact: REGISTRATION_CONTACT };
  const email = tab === "joining" && canShowJoining
    ? joiningInstructionsEmail({ ...data, meetingLink })
    : confirmationEmail(data);

  // Rendered at the end of <body> so no animated or transformed parent can trap the overlay
  return createPortal(
    <div className="wa-modal-backdrop" onClick={onClose}>
      <div className="wa-modal wa-modal--wide" role="dialog" aria-modal="true" aria-labelledby="wa-preview-title" onClick={e => e.stopPropagation()}>
        <div className="wa-modal-head">
          <div>
            <h2 id="wa-preview-title">Email preview</h2>
            <p className="wa-muted">Example for {sample.firstName} ({sample.reference}). Each attendee gets their own name and reference.</p>
          </div>
          <button ref={closeRef} type="button" className="wa-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="wa-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "joining"} disabled={!canShowJoining} onClick={() => setTab("joining")}>
            Joining instructions
          </button>
          <button type="button" role="tab" aria-selected={tab === "confirmation"} onClick={() => setTab("confirmation")}>
            Registration confirmation
          </button>
        </div>
        <p className="wa-preview-subject"><span>Subject</span> {email.subject}</p>
        {/* sandbox with no permissions: the preview can't run scripts or navigate */}
        <iframe className="wa-preview-frame" title={`Preview: ${email.subject}`} sandbox="" srcDoc={email.html} />
      </div>
    </div>,
    document.body,
  );
}
